import { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL

/* Hook simple para detectar si estamos en tamaño movil (mismo patron que
   Login.jsx). El layout de 2 columnas (Units a la izquierda con ancho
   fijo de 220px + lista de lecciones a la derecha) no cabia en pantallas
   de celular y obligaba a desplazar la pantalla hacia un lado. */
function useEsMovil() {
  const [esMovil, setEsMovil] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 820 : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)')
    const handler = e => setEsMovil(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return esMovil
}

function getNivel(xp, t) {
  if (xp < 500)   return { nombre: t.xpBeginner,     color: '#6B7280', siguiente: 500 }
  if (xp < 1500)  return { nombre: t.xpBasic,        color: '#3B82F6', siguiente: 1500 }
  if (xp < 3000)  return { nombre: t.xpIntermediate, color: '#8B5CF6', siguiente: 3000 }
  if (xp < 6000)  return { nombre: t.xpAdvanced,     color: '#F59E0B', siguiente: 6000 }
  return           { nombre: t.xpExpert,              color: '#22C55E', siguiente: 9999 }
}

// Textos bilingues de esta pantalla, controlados por el selector 🇪🇸 ES / 🇬🇧 EN
// que ya existe en la barra lateral del Dashboard.
const TEXTOS = {
  es: {
    cargando:'Cargando currículo...', tuNivel:'Tu nivel', xpTotal:'XP Total', lecciones:'Lecciones',
    unidades:'Unidades', examenUnidadAprobado:'Examen de unidad aprobado', examen:'EXAMEN',
    repasar:'Repasar', empezar:'Empezar', completaAnterior:'Completa', leccionAnterior:'la leccion anterior',
    xpBeginner:'Principiante', xpBasic:'Basico', xpIntermediate:'Intermedio', xpAdvanced:'Avanzado', xpExpert:'Experto'
  },
  en: {
    cargando:'Loading curriculum...', tuNivel:'Your level', xpTotal:'Total XP', lecciones:'Lessons',
    unidades:'Units', examenUnidadAprobado:'Unit exam passed', examen:'EXAM',
    repasar:'Review', empezar:'Start', completaAnterior:'Complete', leccionAnterior:'previous lesson',
    xpBeginner:'Beginner', xpBasic:'Basic', xpIntermediate:'Intermediate', xpAdvanced:'Advanced', xpExpert:'Expert'
  }
}

export default function Curriculo({ usuario, idioma = 'es', onIniciarLeccion }) {
  const t = TEXTOS[idioma] || TEXTOS.es
  const [niveles,      setNiveles]      = useState([])
  const [nivelActivo,  setNivelActivo]  = useState(null)
  const [unidades,     setUnidades]     = useState([])
  const [unidadActiva, setUnidadActiva] = useState(null)
  const [lecciones,    setLecciones]    = useState([])
  const [progreso,     setProgreso]     = useState([])
  const [estadisticas, setEstadisticas] = useState({ xp_total: 0, lecciones_completadas: 0 })
  const [cargando,     setCargando]     = useState(true)
  const [examenesAprobados, setExamenesAprobados] = useState({})
  const esMovil = useEsMovil()

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/curriculo/niveles`),
      usuario
        ? axios.get(`${API}/progreso/usuario/${usuario.id}`).catch(() => ({ data: { progreso_lecciones: [], estadisticas: { xp_total: 0, lecciones_completadas: 0 } } }))
        : Promise.resolve({ data: { progreso_lecciones: [], estadisticas: { xp_total: 0, lecciones_completadas: 0 } } })
    ]).then(([niv, prog]) => {
      setNiveles(niv.data.datos || [])
      setProgreso(prog.data.progreso_lecciones || [])
      setEstadisticas(prog.data.estadisticas || { xp_total: 0, lecciones_completadas: 0 })
      if (niv.data.datos?.length > 0) setNivelActivo(niv.data.datos[0])
    }).finally(() => setCargando(false))
  }, [usuario])

  useEffect(() => {
    if (!nivelActivo) return
    setUnidades([]); setUnidadActiva(null); setLecciones([])
    axios.get(`${API}/curriculo/niveles/${nivelActivo.id}/unidades`)
      .then(r => {
        setUnidades(r.data.datos || [])
        if (r.data.datos?.length > 0) setUnidadActiva(r.data.datos[0])
      })
  }, [nivelActivo])

  useEffect(() => {
    if (!unidadActiva) return
    setLecciones([])
    axios.get(`${API}/curriculo/unidades/${unidadActiva.id}/lecciones`)
      .then(r => setLecciones(r.data.datos || []))

    // Verificar si el examen de esta unidad está aprobado
    if (usuario?.id) {
      axios.get(`${API}/curriculo/unidades/${unidadActiva.id}/examen/resultado/${usuario.id}`)
        .then(r => {
          if (r.data.aprobado) {
            setExamenesAprobados(prev => ({ ...prev, [unidadActiva.id]: true }))
          }
        })
        .catch(() => {})
    }
  }, [unidadActiva, usuario])

  function leccionCompletada(leccionId) {
    return progreso.some(p => p.leccion_id === leccionId && p.estado === 'completada')
  }

  function xpLeccion(leccionId) {
    const p = progreso.find(p => p.leccion_id === leccionId)
    return p?.xp_ganado || 0
  }

  function leccionDesbloqueada(lecciones, index) {
    if (index === 0) return true
    return leccionCompletada(lecciones[index - 1]?.id)
  }

  function handleIniciarLeccion(leccion, index) {
    if (!onIniciarLeccion) return
    const esUltima = index === lecciones.length - 1
    onIniciarLeccion(leccion.id, {
      unidadId:       unidadActiva?.id,
      unidadNombre:   unidadActiva?.titulo,
      esUltimaLeccion: esUltima
    })
  }

  const nivelInfo = getNivel(estadisticas.xp_total, t)
  const pctNivel = Math.min(100, Math.round((estadisticas.xp_total / nivelInfo.siguiente) * 100))

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, gap:16, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E6F0', borderTopColor:'#1B3A8C', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
      {t.cargando}
    </div>
  )

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }}>

      <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:16, padding:24, marginBottom:28, color:'#fff', display:'flex', alignItems:'center', gap:24, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ fontSize:12, opacity:.7, marginBottom:4, textTransform:'uppercase', letterSpacing:1 }}>{t.tuNivel}</div>
          <div style={{ fontSize:22, fontWeight:800 }}>{nivelInfo.nombre}</div>
          <div style={{ marginTop:8, background:'rgba(255,255,255,0.15)', borderRadius:999, height:6, overflow:'hidden' }}>
            <div style={{ width:`${pctNivel}%`, height:'100%', background: nivelInfo.color, borderRadius:999, transition:'width 1s ease' }} />
          </div>
          <div style={{ fontSize:11, opacity:.6, marginTop:4 }}>{estadisticas.xp_total} / {nivelInfo.siguiente} XP</div>
        </div>
        <div style={{ display:'flex', gap:20 }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:28, fontWeight:800, color:'#F07C1E' }}>⚡{estadisticas.xp_total}</div>
            <div style={{ fontSize:11, opacity:.7 }}>{t.xpTotal}</div>
          </div>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:28, fontWeight:800 }}>📚{estadisticas.lecciones_completadas}</div>
            <div style={{ fontSize:11, opacity:.7 }}>{t.lecciones}</div>
          </div>
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:20, overflowX:'auto', paddingBottom:4 }}>
        {niveles.map(n => (
          <button key={n.id} onClick={() => setNivelActivo(n)}
            style={{ padding:'8px 18px', borderRadius:999, border:`2px solid ${nivelActivo?.id===n.id ? '#1B3A8C' : '#E2E6F0'}`, background: nivelActivo?.id===n.id ? '#1B3A8C' : '#fff', color: nivelActivo?.id===n.id ? '#fff' : '#374151', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            {n.nombre}
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: esMovil ? '1fr' : '220px 1fr', gap:20 }}>
        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:16, height:'fit-content' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>{t.unidades}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {unidades.map((u, i) => {
              const leccCompletadas = progreso.filter(p =>
                lecciones.some(l => l.id === p.leccion_id) && p.estado === 'completada'
              ).length
              const pct = lecciones.length > 0 ? Math.round((leccCompletadas / lecciones.length) * 100) : 0
              const examenAprobado = examenesAprobados[u.id]
              return (
                <button key={u.id} onClick={() => setUnidadActiva(u)}
                  style={{ display:'flex', flexDirection:'column', padding:'10px 12px', borderRadius:10, border:`1.5px solid ${unidadActiva?.id===u.id ? '#1B3A8C' : 'transparent'}`, background: unidadActiva?.id===u.id ? '#E8EFFE' : 'transparent', cursor:'pointer', textAlign:'left', gap:4 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:'#F07C1E' }}>U{i+1}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:'#1A1A2E', flex:1 }}>{u.titulo}</span>
                    {examenAprobado && <span style={{ fontSize:12 }}>✅</span>}
                  </div>
                  {pct > 0 && (
                    <div style={{ background:'#E2E6F0', borderRadius:999, height:3, overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background: pct===100 ? '#22C55E' : '#1B3A8C', borderRadius:999 }} />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          {unidadActiva && (
            <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20 }}>
              <div style={{ marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <h2 style={{ fontSize:18, fontWeight:700, color:'#1B3A8C' }}>{unidadActiva.titulo}</h2>
                  {unidadActiva.descripcion && <p style={{ fontSize:13, color:'#6B7280', marginTop:4 }}>{unidadActiva.descripcion}</p>}
                </div>
                {examenesAprobados[unidadActiva.id] && (
                  <div style={{ background:'#F0FDF4', border:'1px solid #22C55E', borderRadius:10, padding:'6px 14px', fontSize:12, fontWeight:700, color:'#166534' }}>
                    ✅ {t.examenUnidadAprobado}
                  </div>
                )}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {lecciones.map((l, i) => {
                  const completada = leccionCompletada(l.id)
                  const desbloqueada = leccionDesbloqueada(lecciones, i)
                  const xpGanado = xpLeccion(l.id)
                  const esUltima = i === lecciones.length - 1
                  return (
                    <div key={l.id}
                      style={{ display:'flex', alignItems:'center', gap: esMovil ? 10 : 16, padding: esMovil ? '12px 14px' : '14px 18px', border:`1.5px solid ${completada ? '#22C55E' : desbloqueada ? '#E2E6F0' : '#F3F4F6'}`, borderRadius:12, background: completada ? '#F0FDF4' : desbloqueada ? '#fff' : '#F9FAFB', opacity: desbloqueada ? 1 : 0.6 }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background: completada ? '#22C55E' : desbloqueada ? '#E8EFFE' : '#E2E6F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, fontWeight:700, color: completada ? '#fff' : '#6B7280' }}>
                        {completada ? '✓' : desbloqueada ? i+1 : '🔒'}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:14, fontWeight:600, color: desbloqueada ? '#1A1A2E' : '#9CA3AF' }}>{l.titulo}</span>
                          {esUltima && <span style={{ fontSize:10, background:'#FEF0E0', color:'#C45F0A', borderRadius:999, padding:'2px 8px', fontWeight:700 }}>{t.examen}</span>}
                        </div>
                        <div style={{ display:'flex', gap:12, marginTop:3 }}>
                          <span style={{ fontSize:12, color:'#6B7280' }}>⏱ {l.duracion_min || 25} min</span>
                          {completada
                            ? <span style={{ fontSize:12, color:'#22C55E', fontWeight:600 }}>⚡ +{xpGanado} XP</span>
                            : <span style={{ fontSize:12, color:'#F07C1E' }}>⚡ {l.xp_total || 90} XP</span>
                          }
                        </div>
                      </div>
                      {desbloqueada && (
                        <button onClick={() => handleIniciarLeccion(l, i)}
                          style={{ background: completada ? '#E8EFFE' : '#1B3A8C', color: completada ? '#1B3A8C' : '#fff', border:'none', borderRadius:10, padding: esMovil ? '8px 12px' : '8px 18px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                          {completada ? t.repasar : t.empezar}
                        </button>
                      )}
                      {!desbloqueada && (
                        <div style={{ fontSize:11, color:'#9CA3AF', textAlign:'right' }}>{t.completaAnterior}<br/>{t.leccionAnterior}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
